import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

export type PillTone = "success" | "warn" | "danger" | "info" | "neutral";

const TONES: Record<PillTone, string> = {
  // Money green, not the brand teal. "In Stock" and "Paid" are statements about
  // money and goods; the teal is the product's identity and says nothing about
  // whether a thing went well.
  success: "bg-money-50 text-money-700",
  warn: "bg-warn-50 text-warn-700",
  danger: "bg-danger-50 text-danger-700",
  info: "bg-info-50 text-info-500",
  neutral: "bg-canvas text-ink-600",
};

export interface PillProps {
  tone?: PillTone;
  children: ReactNode;
  /** A small leading dot, as used by the Inventory table's Category column. */
  dot?: boolean;
  className?: string;
}

export function Pill({ tone = "neutral", dot = false, children, className }: PillProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-medium",
        TONES[tone],
        className,
      )}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

export type StockStatus = "IN_STOCK" | "LOW_STOCK" | "OUT_OF_STOCK";

const STOCK_LABELS: Record<StockStatus, { label: string; tone: PillTone }> = {
  IN_STOCK: { label: "In Stock", tone: "success" },
  LOW_STOCK: { label: "Low Stock", tone: "warn" },
  OUT_OF_STOCK: { label: "Out of Stock", tone: "danger" },
};

/**
 * The Inventory table's Status column.
 *
 * Colour is never the only signal — each state has a distinct word — because a
 * red/green distinction is invisible to roughly one man in twelve, and stock
 * status is exactly the sort of thing scanned at a glance.
 */
export function StockStatusPill({ status }: { status: StockStatus }) {
  const { label, tone } = STOCK_LABELS[status];
  return <Pill tone={tone}>{label}</Pill>;
}
